import React, {Component} from 'react';
import {APIClient} from "../../utils/APIClient";
import logo from '../../images/logo_dashbo_purple.svg';
import {API_URL} from "../../settings/local_settings";
import {AgencyFormFieldError} from "./AgencyFormFieldError";
import {gaEvents} from "../../utils/GA4Events";
import {metaEvents} from "../../utils/MetaPixelEvents";
import {Spinner} from "../Spinner";
import {withRouterParams} from "../../utils/withRouterParams";
import { withTranslation } from 'react-i18next';
import {LocalStorage} from "../../utils/LocalStorage";
import {detectCountry} from "../../utils/CountryDetection";
import {isOk, isNetworkError} from "../../utils/apiResponse";
import {LoadErrorState} from "../elements/LoadErrorState";
import {CaretDown, Check, SignOut} from "../icons";
import {Stepper} from "../onboarding/Stepper";
import {clearLeadTokenFresh, fieldErrorsOf, isLeadTokenFresh, readLeadPrefill} from "./leadPrefill";
// Las clases del túnel (título, tarjeta, botón principal) se reusan tal cual: la pantalla habla el
// mismo lenguaje visual por construcción (card VELQYFFQ). Ver el comentario de `AgencyRegister.scss`.
import '../onboarding/onboarding.scss';
import './AgencyRegister.scss';
import {CLARITY_UNMASK} from '../../utils/clarity/unmask';
import {toast} from 'react-toastify';
import {
    ORIGIN_SIGNUP, applyRedeemedTrial, dayAndMonth, isDefinitiveRejection, redeemTrialCode, trialCodeErrorKey,
} from '../trial_code/trialCodeApi';

// Cuánto espera el alta al código de prueba antes de redirigir igual (review C-cy-t-01).
export const SIGNUP_TRIAL_CODE_WAIT_MS = 4000;

class AgencyRegister extends Component {
    constructor(props) {
        super(props);
        this.redirectAgencyConnections = this.redirectAgencyConnections.bind(this);
        this.useAnotherAccount = this.useAnotherAccount.bind(this);
    }
    apiUrl() {
        return API_URL + 'agency-register/';
    }

    redirectAgencyConnections() {
        // Verificar si hay una URL guardada para redirigir después del registro
        const redirectUrl = LocalStorage.getRedirectUrl();
        if (redirectUrl) {
            LocalStorage.clearRedirectUrl();
            this.props.history.push(redirectUrl);
        } else {
            // La agencia recién creada pasa por la puerta del túnel de alta, que decide a dónde va
            // de verdad: si le corresponde la experiencia 2.1 arranca el recorrido, y si no,
            // reproduce este mismo destino de siempre con su estado. Va acá y no a `/agency/conections`
            // porque el payload de módulos que hay en memoria se pidió cuando el usuario todavía no
            // tenía agencia: quien puede decidir es la puerta, después de volver a preguntarlo.
            this.props.history.push({
                pathname: '/onboarding',
                state: { showOnBoarding: true }
            });
        }
    }
    /**
     * La única salida del registro. Sin ella, quien entró con la cuenta de Google equivocada no
     * tenía cómo cambiarla: la pantalla no tiene menú. Vuelve a `/signup` —no a `/login`— porque
     * está a mitad de un alta, y conserva el token del lead que llegó en esta pestaña.
     */
    useAnotherAccount() {
        LocalStorage.clearSessionForAccountSwitch();
        this.props.history.push('/signup');
    }

    render() {
        const {t} = this.props;
        return (
            <div className="ob-tunnel agr-page">
                {/* La misma barra del túnel: el registro es su paso 1, y lo dice (recorrido del
                    02/10). La salida a la derecha, como «Guardar y seguir después» en los pasos,
                    deja al stepper en el mismo lugar y no salta al pasar al paso 2. */}
                <header className="ob-topbar">
                    <img className="ob-topbar__logo" src={logo} alt="Dashbo"/>
                    <Stepper current="agency"/>
                    <button type="button" className="ob-exit" onClick={this.useAnotherAccount}>
                        <SignOut size={15}/>
                        <span>{t('agency_register.use_another_account')}</span>
                    </button>
                </header>
                <main className="agr-body">
                    <TranslatedAgencyForm submit_url={this.apiUrl()}
                                          redirectAgencyConnections={this.redirectAgencyConnections}/>
                </main>
            </div>
        );
    }
}

class AgencyForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            saving: false,
            countries: [],
            errors: [],
            // `null` = sin error; un string = con error. Se compara `!== null` y nunca por truthy,
            // porque `''` también es un error válido.
            loadError: null,
            submitError: null,
            // Lo que el formulario de dashbo.io ya preguntó (`readLeadPrefill`). `undefined` mientras
            // se pregunta, `null` si no hay lead o no trajo nada. La pantalla espera la respuesta
            // antes de dibujar el formulario: los campos no tienen control y el valor inicial sólo
            // se lee al montarlos, así que dibujarlos antes los dejaba vacíos.
            prefill: undefined,
            // D4: con el lead completo la agencia se crea sin mostrar el formulario.
            creatingFromLead: false,
            // Teléfono y sitio web van plegados (D5). Se despliegan solos si el backend marca un
            // error en uno de los dos: un error escondido es un alta que no avanza sin decir por qué.
            optionalOpen: false
        };
        this.handleSubmit = this.handleSubmit.bind(this);
        this.getCountries = this.getCountries.bind(this);
        this.toggleOptional = this.toggleOptional.bind(this);
        this.leadCreationStarted = false;
    }

    componentDidMount() {
        this.getCountries();
        this.getLeadPrefill();
    }

    /**
     * La lista de países, que gatea toda la pantalla.
     *
     * Si no llega no hay formulario, así que el fallo se DICE con el bloque de carga y su
     * reintentar. Antes era un `alert()` —legacy, bloqueante y con el `error_message` crudo, que
     * puede ser el `detail` en inglés de DRF— y detrás quedaba un spinner que no terminaba nunca:
     * la primera pantalla del producto se colgaba sin explicar nada.
     */
    getCountries() {
        this.setState({loadError: null});
        new APIClient(API_URL + 'countries/').call('GET').then((data) => {
            if (isOk(data)) {
                this.setState({countries: data['countries']}, () => this.maybeCreateFromLead());
                return;
            }
            const {t} = this.props;
            this.setState({loadError: isNetworkError(data) ? t('agency_register.error_network')
                                                           : t('agency_register.load_error_explanation')});
        });
    }

    /**
     * El salto «web → app» (D4). El sitio dejó el token del lead en /signup; con él se pregunta qué
     * datos ya cargó la agencia en dashbo.io, para no pedírselos dos veces.
     *
     * Cualquier respuesta que no sea un prefill —sin lead, token vencido, usuario con agencia, la
     * red o el endpoint todavía sin deployar— deja el formulario vacío y sin aviso: es un atajo, y
     * su ausencia no es un error que el usuario tenga que leer.
     *
     * Sólo se pregunta con un token que llegó de dashbo.io en ESTA pestaña: uno heredado de otra
     * sesión del navegador puede ser de otra persona (ver `isLeadTokenFresh`).
     */
    getLeadPrefill() {
        const token = LocalStorage.getSignupLeadToken();
        if (!token || !isLeadTokenFresh(token)) {
            this.setState({prefill: null});
            return;
        }
        new APIClient(API_URL + 'agency-lead/prefill/?token=' + encodeURIComponent(token)).call('GET')
            .then((data) => {
                this.setState({prefill: readLeadPrefill(data)}, () => this.maybeCreateFromLead());
            });
    }

    /**
     * Con el lead completo, la agencia se crea sola. Espera a los países, porque de esa lista sale
     * el país que el backend exige, y corre UNA vez: si el alta falla, el usuario ve el formulario
     * prellenado con el error y lo que sigue lo decide él con su clic.
     *
     * ⚠️ La guarda es de instancia y no del state, y no es sólo para el montaje doble de
     * `StrictMode`: las dos respuestas (países y prefill) pueden aterrizar en el MISMO render, y
     * entonces los dos callbacks de `setState` ven todo listo y postean dos veces. Medido: sin la
     * guarda caen tres tests de `agencyRegisterLead`, no uno.
     */
    maybeCreateFromLead() {
        const {prefill, countries} = this.state;
        if (this.leadCreationStarted || !prefill || !prefill.complete || countries.length === 0) return;
        this.leadCreationStarted = true;
        this.setState({creatingFromLead: true});
        this.submitAgency(this.buildAgencyPayload({
            name: prefill.agencyName,
            creatorName: prefill.contactName,
            phone: prefill.phone,
            website: prefill.website
        }));
    }

    buildAgencyPayload({name, creatorName, phone, website}) {
        const payload = {
            'name': name,
            'creator_name': creatorName,
            'phone_number': phone,
            'website': website,
            // El codigo de referencia salio del alta (2026-09-22): solo generaba la pregunta
            // «¿que tengo que hacer aca?». No se manda mas — el form del backend lo tiene
            // `required=False` y nadie lo leia despues.
            // El pais ya no se pregunta, pero se sigue mandando: el backend lo tiene NOT NULL
            // y de ahi sale la moneda por defecto de la agencia. Se detecta del navegador y se
            // valida contra la lista que devuelve el backend, para no mandar algo que rebote.
            'country': detectCountry(this.state.countries.map((country) => country[0])),
        };

        // De qué campaña vino esta agencia. Lo dejó el sitio en /signup?token=...
        // Si no hay token, la clave directamente no viaja: es un dato opcional y no tiene
        // sentido hacer que el backend lo interprete como vacío.
        const leadToken = LocalStorage.getSignupLeadToken();
        if (leadToken) {
            payload['lead_token'] = leadToken;
        }

        return payload;
    }

    handleSubmit(event) {
        event.preventDefault();
        const form = event.target;
        // Teléfono y sitio web están siempre en el DOM, aunque plegados (`hidden`): el acceso por
        // nombre no puede encontrar un campo que no existe, y un `undefined.value` tumba el alta.
        this.submitAgency(this.buildAgencyPayload({
            name: form.name.value,
            creatorName: form.creatorname.value,
            phone: form.phonenumber.value,
            website: form.website.value
        }));
    }

    /**
     * El alta, con TODAS las respuestas contempladas.
     *
     * ⚠️ `status === 'ERROR'` es un colador: el backend escribe en `status` el código TIPADO del
     * error (`EMAIL_IS_AGENCY_USER`, `ERROR_NO_AGENCY`…) y el propio `APIClient` inventa un
     * `NETWORK_ERROR`. Ninguno vale `'ERROR'`, así que el `if/else if` que había acá se los comía
     * EN SILENCIO: el spinner volvía al botón y la pantalla no decía nada — en el alta, que es la
     * primera pantalla del producto y no tiene a dónde reintentar.
     *
     * El mensaje es SIEMPRE genérico y nunca el `error_message` del backend: por ahí se filtraría
     * si un email ya pertenece a otra agencia, que es justo lo que el aislamiento cross-agency
     * prohíbe distinguir. Lo único que elige el copy es si el fallo fue de red.
     *
     * Un fallo del alta automática (D4) cae acá igual: `creatingFromLead` vuelve a `false` y el
     * formulario aparece prellenado, con el error a la vista y los opcionales desplegados: lo que
     * se mandó tiene que verse entero, porque el usuario no lo tipeó en esta pantalla.
     */
    submitAgency(payload) {
        this.setState({saving: true, submitError: null, errors: []});
        new APIClient(this.props.submit_url).call('POST', payload)
            .then((data) => {
                if (isOk(data)) {
                    LocalStorage.removeSignupLeadToken();
                    clearLeadTokenFresh();
                    gaEvents.eventAgencyCreated();
                    metaEvents.eventAgencyCreated();
                    const redeeming = this.redeemSignupTrialCode();
                    if (!redeeming) {
                        this.setState({saving: false});
                        this.props.redirectAgencyConnections();
                        return;
                    }
                    // Con un código, se aplica ANTES de redirigir y el botón sigue girando (un segundo clic
                    // pediría crear otra agencia). Ver `redeemSignupTrialCode`.
                    redeeming.then(() => {
                        this.setState({saving: false});
                        this.props.redirectAgencyConnections();
                    });
                    return;
                }
                // Los errores POR CAMPO los pinta `AgencyFormFieldError` debajo de cada input. Sólo
                // cuentan si nombran algún campo: un código tipado llega con `errors: "{}"` y un 401
                // de DRF con `errors: []` (ver `fieldErrorsOf`), y los dos van al aviso general.
                const errors = fieldErrorsOf(data);
                if (Object.keys(errors).length > 0) {
                    this.showSubmitFailure({errors}, 'phone_number' in errors || 'website' in errors);
                    return;
                }
                const {t} = this.props;
                this.showSubmitFailure({submitError: isNetworkError(data) ? t('agency_register.error_network')
                                                                          : t('agency_register.error_generic')});
            })
            .catch(() => {
                // Red de seguridad: si algo de arriba lanza, el botón no puede quedar girando para
                // siempre ni la pantalla clavada en «Creando tu agencia…».
                this.showSubmitFailure({submitError: this.props.t('agency_register.error_generic')});
            });
    }

    /**
     * El código de prueba que llegó en `/signup?codigo=...` (CY-T): con la agencia recién creada, se aplica
     * solo. Devuelve `null` si no hay código, o una promesa que NUNCA rechaza y resuelve como mucho a los
     * `SIGNUP_TRIAL_CODE_WAIT_MS`: el alta espera a que termine antes de redirigir.
     *
     * - **Antes del redirect, y no a la par** (review C-cy-t-01): la puerta del túnel pide los módulos, que es
     *   lo que crea la suscripción, y el canje también la crea. A la par chocaban en el uno-a-uno de
     *   `AgencySubscription`, los módulos daban 500 y la agencia del Cyber caía al camino clásico.
     * - **Nunca rechaza**: está dentro del camino de éxito del alta, cuyo `.catch` mostraría «no pudimos crear
     *   tu agencia» sobre una agencia ya creada.
     * - **El código se borra sólo con un OK o un «no» definitivo** (review C-cy-t-04): ante la red, un 500 o un
     *   backend sin deployar queda guardado, y el banner lo ofrece prellenado al salir del túnel.
     */
    redeemSignupTrialCode() {
        let code = null;
        try {
            code = LocalStorage.getSignupTrialCode();
        } catch (e) {
            return null;
        }
        if (!code) return null;
        const {t} = this.props;
        const redeemed = redeemTrialCode(code, ORIGIN_SIGNUP).then((response) => {
            if (isOk(response) && response.data) {
                applyRedeemedTrial(response.data);
                toast.success(t('trial_code.signup_applied', {
                    date: dayAndMonth(response.data.to_date), interpolation: {escapeValue: false},
                }));
                return;
            }
            if (isDefinitiveRejection(response)) LocalStorage.removeSignupTrialCode();
            toast.error(t(trialCodeErrorKey(response)));
        }).catch(() => {});
        const waitAtMost = new Promise((resolve) => setTimeout(resolve, SIGNUP_TRIAL_CODE_WAIT_MS));
        return Promise.race([redeemed, waitAtMost]);
    }

    showSubmitFailure(failure, openOptional = false) {
        this.setState(prev => ({
            ...failure,
            saving: false,
            creatingFromLead: false,
            optionalOpen: prev.optionalOpen || openOptional || prev.creatingFromLead
        }));
    }

    toggleOptional() {
        this.setState(prev => ({optionalOpen: !prev.optionalOpen}));
    }

    renderField({id, name, label, errorField, defaultValue, ...inputProps}) {
        return (
            <div className="agr-field">
                <label htmlFor={id} {...CLARITY_UNMASK}>{label}</label>
                <input id={id} name={name} type="text" defaultValue={defaultValue} {...inputProps}/>
                <AgencyFormFieldError errors={this.state.errors} fieldName={errorField}/>
            </div>
        );
    }

    renderForm() {
        const {t} = this.props;
        const prefill = this.state.prefill || {};
        return (
            <div className="agr-card">
                {/* El cartel del trial, compacto y ANTES del título: es la objeción que frena el
                    alta (tarjeta y costo) y se resuelve en una línea. Hasta el 30/09 era una caja
                    con ícono y dos renglones que competía con el formulario. */}
                <p className="agr-trial">
                    <Check size={14} weight="bold" aria-hidden="true"/>
                    <span {...CLARITY_UNMASK}>{t('agency_register.trial_note')}</span>
                </p>
                <div className="ob-head agr-head">
                    <h1 {...CLARITY_UNMASK}>{t('agency_register.title')}</h1>
                    <p {...CLARITY_UNMASK}>{t('agency_register.subtitle')}</p>
                </div>
                <form onSubmit={this.handleSubmit}>
                    {this.renderField({
                        id: 'agr-name', name: 'name', errorField: 'name',
                        label: t('agency_register.agency_name'), defaultValue: prefill.agencyName,
                        required: true, maxLength: 50, autoComplete: 'organization'
                    })}
                    {this.renderField({
                        id: 'agr-creator', name: 'creatorname', errorField: 'creator_name',
                        label: t('agency_register.your_name'), defaultValue: prefill.contactName,
                        required: true, maxLength: 100, autoComplete: 'name'
                    })}

                    <button type="button" className="agr-optional-toggle" {...CLARITY_UNMASK}
                            aria-expanded={this.state.optionalOpen} aria-controls="agr-optional"
                            onClick={this.toggleOptional}>
                        <CaretDown size={13} weight="bold" aria-hidden="true"/>
                        {t('agency_register.optional_toggle')}
                    </button>
                    <div id="agr-optional" className="agr-optional" hidden={!this.state.optionalOpen}>
                        {this.renderField({
                            id: 'agr-phone', name: 'phonenumber', errorField: 'phone_number',
                            label: t('agency_register.phone'), defaultValue: prefill.phone,
                            // Los largos del modelo (`Agency._phone_number` 50, `_website` 200): el
                            // formulario de dashbo.io admite más, y sin tope el alta rebotaba por un
                            // campo que el usuario no ve plegado.
                            maxLength: 50, autoComplete: 'tel'
                        })}
                        {this.renderField({
                            id: 'agr-website', name: 'website', errorField: 'website',
                            label: t('agency_register.website'), defaultValue: prefill.website,
                            maxLength: 200, autoComplete: 'url'
                        })}
                    </div>

                    {/* Lo que el backend contestó y no es de un campo puntual. Va pegado al
                        botón que se apretó, no arriba de todo: es la respuesta a ese clic. */}
                    {this.state.submitError !== null && (
                        <p className="form-submit-error agr-error" role="alert">{this.state.submitError}</p>
                    )}
                    <button type="submit" className="ob-btn -primary agr-submit" {...CLARITY_UNMASK}
                            disabled={this.state.saving} aria-busy={this.state.saving}>
                        {this.state.saving ? <Spinner color="negative small"/> : t('agency_register.submit')}
                    </button>
                    {/* Qué plataformas se conectan, en una línea. Lo que sigue ya lo dice el
                        stepper («Tus plataformas»); acá se responde «¿está la mía?», y por eso
                        nombra también GA4 y la tienda aunque el paso 2 sólo tome las de anuncios
                        (el paso lo explica con `LaterPlatformsCard`). La señal de confianza que
                        iba debajo la sacó fede el 02/10. */}
                    <p className="agr-next" {...CLARITY_UNMASK}>{t('agency_register.next_step')}</p>
                </form>
            </div>
        );
    }

    render() {
        const { t } = this.props;
        if (this.state.loadError !== null) {
            // Si la lista de países no llega no hay formulario que mostrar, así que la pantalla lo
            // dice y ofrece reintentar, en vez de quedarse en un spinner eterno.
            return (
                <div className="agr-card">
                    <LoadErrorState title={t('agency_register.load_error')}
                                    text={this.state.loadError}
                                    retryLabel={t('agency_register.load_error_retry')}
                                    onRetry={this.getCountries}/>
                </div>
            );
        }
        if (this.state.creatingFromLead) {
            // D4: el usuario ya completó el formulario en dashbo.io. Se le dice qué se está
            // creando, con el nombre que escribió, para que el salto no se lea como una pantalla
            // que se saltó sola.
            return (
                <div className="agr-card agr-creating" role="status">
                    <Spinner color="positive"/>
                    <p>{t('agency_register.creating_from_lead', {
                        name: this.state.prefill.agencyName, interpolation: {escapeValue: false}
                    })}</p>
                </div>
            );
        }
        // Se sigue esperando la lista de paises aunque ya no haya select: es contra esa lista
        // que se valida el pais detectado antes de mandarlo.
        if (this.state.countries.length > 0 && this.state.prefill !== undefined) {
            return this.renderForm();
        }
        // El spinner del design system y no un «Cargando ...» suelto, que es lo que la
        // convención del repo prohíbe explícitamente. Es la PRIMERA pantalla que ve una agencia
        // nueva: un texto pelado arriba a la izquierda del panel se lee como una página rota.
        return (
            <div className="box-loader">
                <Spinner color="positive"/>
            </div>
        );
    }
}

const TranslatedAgencyForm = withTranslation()(AgencyForm);

export { TranslatedAgencyForm as AgencyForm };

export default withRouterParams(withTranslation()(AgencyRegister));
